<template>
  <div class="descargar-catalogo-container">
    <div class="config-card">
      <div class="card-header">
        <i class="mdi mdi-file-pdf-box"></i>
        <h1>Configurar Catálogo PDF</h1>
      </div>

      <p class="descripcion">
        Personaliza tu catálogo antes de descargarlo. Incluirá fotos, nombres, 
        códigos, tallas y toda la información de cada producto.
      </p>

      <div class="opcion-grupo">
        <div class="opcion-item">
          <div class="opcion-header">
            <i class="mdi mdi-warehouse"></i>
            <span>Mostrar disponibilidad</span>
          </div>
          <label class="switch-container">
            <input type="checkbox" v-model="mostrarDisponibilidad" />
            <span class="switch"></span>
            <span class="switch-label">{{ mostrarDisponibilidad ? 'Sí' : 'No' }}</span>
          </label>
        </div>
      </div>

      <div class="opcion-grupo">
        <div class="opcion-item">
          <div class="opcion-header">
            <i class="mdi mdi-image-multiple"></i>
            <span>Incluir todas las fotos disponibles</span>
          </div>
          <label class="switch-container">
            <input type="checkbox" v-model="incluirTodasFotos" />
            <span class="switch"></span>
            <span class="switch-label">{{ incluirTodasFotos ? 'Hasta 10 por referencia' : 'Solo la primera disponible' }}</span>
          </label>
        </div>
        <p class="opcion-ayuda">Las fotos agotadas no se incluyen en ninguna de las dos opciones.</p>
      </div>

      <div class="opcion-grupo">
        <div class="opcion-item">
          <div class="opcion-header">
            <i class="mdi mdi-currency-usd-off"></i>
            <span>Descargar sin precios</span>
          </div>
          <label class="switch-container">
            <input type="checkbox" v-model="sinPrecios" />
            <span class="switch"></span>
            <span class="switch-label">{{ sinPrecios ? 'Sí' : 'No' }}</span>
          </label>
        </div>
      </div>

      <!-- Opción: Categorías -->
      <div class="opcion-grupo">
        <div class="opcion-item">
          <div class="opcion-header">
            <i class="mdi mdi-tag-multiple"></i>
            <span>Todas las categorías</span>
          </div>
          <label class="switch-container">
            <input type="checkbox" v-model="todasCategorias" />
            <span class="switch"></span>
            <span class="switch-label">{{ todasCategorias ? 'Sí' : 'No' }}</span>
          </label>
        </div>

        <!-- Selector de categorías si no se muestran todas -->
        <transition name="slide">
          <div v-if="!todasCategorias" class="categorias-selector">
            <p class="selector-titulo">Selecciona las categorías a incluir:</p>
            <div class="categorias-grid">
              <label 
                v-for="cat in categoriasDisponibles" 
                :key="cat" 
                class="categoria-checkbox"
                :class="{ selected: categoriasSeleccionadas.includes(cat) }"
              >
                <input 
                  type="checkbox" 
                  :value="cat" 
                  v-model="categoriasSeleccionadas" 
                />
                <span class="checkmark"></span>
                <span class="categoria-nombre">{{ cat }}</span>
              </label>
            </div>
          </div>
        </transition>
      </div>

      <!-- Resumen de configuración -->
      <div class="resumen">
        <h3><i class="mdi mdi-clipboard-list"></i> Resumen</h3>
        <ul>
          <li>
            <i class="mdi mdi-check-circle"></i>
            Productos: <strong>{{ productosAIncluir.length }}</strong>
          </li>
          <li>
            <i class="mdi mdi-check-circle"></i>
            Categorías: <strong>{{ todasCategorias ? 'Todas' : categoriasSeleccionadas.length + ' seleccionadas' }}</strong>
          </li>
          <li>
            <i class="mdi mdi-check-circle"></i>
            Disponibilidad: <strong>{{ mostrarDisponibilidad ? 'Sí' : 'No' }}</strong>
          </li>
          <li>
            <i class="mdi mdi-check-circle"></i>
            Precios: <strong>{{ sinPrecios ? 'Sin precio' : 'Con precio' }}</strong>
          </li>
          <li>
            <i class="mdi mdi-check-circle"></i>
            Fotos: <strong>{{ incluirTodasFotos ? 'Todas las disponibles' : 'Primera disponible' }}</strong>
          </li>
        </ul>
      </div>

      <!-- Botón de descarga -->
      <button 
        class="btn-descargar" 
        @click="generarPDF" 
        :disabled="generando || productosAIncluir.length === 0"
      >
        <template v-if="generando">
          <i class="mdi mdi-loading mdi-spin"></i>
          Generando PDF... {{ progresoTexto }}
        </template>
        <template v-else>
          <i class="mdi mdi-download"></i>
          Descargar Catálogo PDF
        </template>
      </button>

      <!-- Barra de progreso -->
      <div v-if="generando" class="progress-container">
        <div class="progress-bar" :style="{ width: progreso + '%' }"></div>
      </div>

      <p v-if="productosAIncluir.length === 0" class="advertencia">
        <i class="mdi mdi-alert"></i>
        No hay productos para incluir. Selecciona al menos una categoría.
      </p>
    </div>
  </div>
</template>

<script>
import HttpService from "@/Servicios/HttpService";
import { jsPDF } from "jspdf";

export default {
  name: "DescargarCatalogo",
  data() {
    return {
      productos: [],
      mostrarDisponibilidad: false,
      sinPrecios: false,
      incluirTodasFotos: false,
      todasCategorias: true,
      categoriasSeleccionadas: [],
      generando: false,
      progreso: 0,
      progresoTexto: "",
    };
  },
  computed: {
    esEmpleado() {
      const rol = localStorage.getItem("rol");
      return rol === "administrador" || rol === "cajero" || rol === "inven";
    },
    categoriasDisponibles() {
      const cats = [...new Set(this.productos.map(p => p.nombreCategoria).filter(Boolean))];
      return cats.sort((a, b) => a.localeCompare(b));
    },
    productosAIncluir() {
      let prods = this.productos.filter(p => this.tieneImagenDisponible(p));
      
      if (!this.todasCategorias) {
        prods = prods.filter(p => this.categoriasSeleccionadas.includes(p.nombreCategoria));
      }
      
      // Ordenar por categoría alfabéticamente, luego por nombre
      return prods.sort((a, b) => {
        const catComp = (a.nombreCategoria || "").localeCompare(b.nombreCategoria || "");
        if (catComp !== 0) return catComp;
        return (a.nombre || "").localeCompare(b.nombre || "");
      });
    },
  },
  async mounted() {
    await this.cargarProductos();
  },
  methods: {
    async cargarProductos() {
      try {
        const res = await HttpService.obtenerConConsultas("productos.php", {
          accion: "listasProductos",
        });
        this.productos = Array.isArray(res) ? res : res.productos || [];
      } catch (e) {
        this.productos = [];
      }
    },
    esImagenPlaceholder(img) {
      const limpia = String(img || "").trim().toLowerCase();
      return (
        !limpia ||
        limpia.includes("sinimagen") ||
        limpia.includes("sin-foto") ||
        limpia.includes("sinfoto") ||
        limpia.includes("placeholder") ||
        limpia.includes("megapos")
      );
    },
    imagenTieneStock(producto, imagenKey) {
      const tallas = producto?.tallasPorImagen?.[imagenKey] || [];
      if (!Array.isArray(tallas) || tallas.length === 0) {
        return Number(producto?.existencia || 0) > 0;
      }
      return tallas.some((t) => Number(t.existencia || 0) > 0);
    },
    tieneImagenDisponible(producto) {
      return this.getAllImages(producto).length > 0;
    },
    getAllImages(producto) {
      const images = [];
      for (let i = 1; i <= 10; i++) {
        const key = `imagen${i}`;
        const img = producto[key];
        if (!this.esImagenPlaceholder(img) && this.imagenTieneStock(producto, key)) {
          const url = img.includes("http") 
            ? img 
            : img.replace("../api/", "https://maxica.com.co/ximena/api/");
          images.push({ url, key });
        }
      }
      return this.incluirTodasFotos ? images : images.slice(0, 1);
    },
    getTallasParaImagen(producto, imagenKey) {
      // Obtener tallas específicas para esta imagen
      const tallasPorImagen = producto.tallasPorImagen || {};
      const tallasData = tallasPorImagen[imagenKey] || [];
      
      // Filtrar tallas vacías o "sin talla"
      const tallasFiltradas = tallasData.filter(
        t => t.talla && t.talla.toLowerCase() !== "sin talla" && Number(t.existencia || 0) > 0
      );

      if (tallasFiltradas.length === 0) {
        return "Talla única";
      }

      // Si se debe mostrar disponibilidad, agregamos la cantidad
      if (this.mostrarDisponibilidad) {
        const labels = tallasFiltradas.map(t => `${t.talla} (${t.existencia})`);
        return labels.join(", ");
      }

      // Si no, solo mostramos las tallas
      const tallas = tallasFiltradas.map(t => t.talla);
      if (tallas.length === 1) {
        return tallas[0];
      }
      return tallas.join(", ");
    },
    async loadImageAsBase64(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const maxSize = 260;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve({ data: canvas.toDataURL("image/jpeg", 0.8), width, height });
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });
    },
    normalizarTallas(lista) {
      if (!Array.isArray(lista)) return [];
      return lista
        .map((t) => (t || "").toString().trim())
        .filter((t) => t && t.toLowerCase() !== "sin talla");
    },
    formatTallasLabel(tallas) {
      const limpias = this.normalizarTallas(tallas);
      if (limpias.length === 0) {
        return "Talla única";
      }
      if (limpias.length === 1) {
        return `Talla: ${limpias[0]}`;
      }
      // Mostrar TODAS las tallas sin truncar
      return `Tallas: ${limpias.join(", ")}`;
    },
    formatPrice(price) {
      return "$" + Number(price || 0).toLocaleString("es-CO");
    },
    getTallasDisponiblesProducto(producto) {
      const tallas = [];
      Object.values(producto.tallasPorImagen || {}).forEach((grupo) => {
        if (!Array.isArray(grupo)) return;
        grupo.forEach((t) => {
          const nombre = String(t.talla || "").trim();
          if (nombre && nombre.toLowerCase() !== "sin talla" && Number(t.existencia || 0) > 0) {
            tallas.push(this.mostrarDisponibilidad ? `${nombre} (${t.existencia})` : nombre);
          }
        });
      });
      return [...new Set(tallas)];
    },
    async generarPDF() {
      if (this.generando) return;
      this.generando = true;
      this.progreso = 0;
      this.progresoTexto = "Preparando...";

      try {
        const doc = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;
        const contentWidth = pageWidth - margin * 2;
        
        // Colores
        const primaryColor = [113, 28, 43];
        const textColor = [32, 32, 32];
        const lightGray = [250, 248, 244];
        const borderColor = [224, 220, 214];
        const mutedColor = [101, 91, 84];

        // Página de portada
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, pageHeight, "F");
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(32);
        doc.setFont("helvetica", "bold");
        doc.text("CATÁLOGO DE PRODUCTOS", pageWidth / 2, pageHeight / 2 - 18, { align: "center" });
        
        doc.setFontSize(18);
        doc.setFont("helvetica", "normal");
        doc.text("Colección 2026", pageWidth / 2, pageHeight / 2 + 2, { align: "center" });
        
        doc.setFontSize(14);
        const fecha = new Date().toLocaleDateString("es-CO", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        doc.text(fecha, pageWidth / 2, pageHeight / 2 + 20, { align: "center" });
        
        doc.setFontSize(12);
        doc.text("Catálogo de productos", pageWidth / 2, pageHeight - 30, { align: "center" });
        
        // Agrupar productos por categoría
        const productosPorCategoria = {};
        this.productosAIncluir.forEach(p => {
          const cat = p.nombreCategoria || "Sin categoría";
          if (!productosPorCategoria[cat]) {
            productosPorCategoria[cat] = [];
          }
          productosPorCategoria[cat].push(p);
        });

        const categorias = Object.keys(productosPorCategoria).sort((a, b) => a.localeCompare(b));
        let totalProductos = this.productosAIncluir.length;
        let productosProcessed = 0;

        for (const categoria of categorias) {
          const productosCategoria = productosPorCategoria[categoria];
          
          // Nueva página para cada categoría
          doc.addPage();
          
          // Encabezado de categoría
          doc.setFillColor(...primaryColor);
          doc.rect(0, 0, pageWidth, 25, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(18);
          doc.setFont("helvetica", "bold");
          doc.text(categoria.toUpperCase(), margin, 17);
          
          let y = 35;
          const cardWidth = contentWidth;

          for (const producto of productosCategoria) {
            productosProcessed++;
            this.progreso = Math.round((productosProcessed / totalProductos) * 100);
            this.progresoTexto = `${productosProcessed}/${totalProductos} productos`;

            let tallas = null;
            if (producto.tallasSeleccionadas) {
              tallas = producto.tallasSeleccionadas;
              if (typeof tallas === "string") {
                try {
                  tallas = JSON.parse(tallas);
                } catch (e) {
                  tallas = tallas.split(",");
                }
              }
            }
            const tallasList = this.getTallasDisponiblesProducto(producto);
            const images = this.getAllImages(producto);
            const imagesPerRow = 3;
            const imgGap = 6;
            const rowGap = 8;
            const labelHeight = 8; // Espacio para 2 líneas de tallas debajo de cada imagen
            const maxGridWidth = cardWidth - 12;
            const imgSize = Math.min(
              40,
              Math.floor((maxGridWidth - (imagesPerRow - 1) * imgGap) / imagesPerRow)
            );
            const rows = images.length ? Math.ceil(images.length / imagesPerRow) : 0;
            const tallasTexto = this.formatTallasLabel(tallasList);
            // Altura de las imágenes con labels individuales
            const rowHeight = imgSize + labelHeight;
            const imagesHeight = rows
              ? rows * rowHeight + (rows - 1) * rowGap
              : 0;

            const nombreLines = doc.splitTextToSize(
              producto.nombre || "Sin nombre",
              contentWidth - 12
            );
            const nombreLineCount = Math.min(nombreLines.length, 2);
            const disponibilidad =
              this.mostrarDisponibilidad && producto.existencia !== undefined
                ? Number(producto.existencia)
                : null;

            const infoLineHeight = 4;
            // Calcular líneas de tallas para altura correcta
            const tallasLinesCount = Math.min(
              doc.splitTextToSize(tallasTexto, contentWidth - 12).length,
              3
            );
            let infoHeight = nombreLineCount * 6 + infoLineHeight; // código
            if (producto.tipo) infoHeight += infoLineHeight;
            infoHeight += infoLineHeight * tallasLinesCount; // todas las líneas de tallas
            if (disponibilidad !== null && !Number.isNaN(disponibilidad)) {
              infoHeight += infoLineHeight;
            }
            if (!this.sinPrecios) {
              infoHeight += 7; // precio principal
              if (this.esEmpleado && producto.precioMayoreo) {
                infoHeight += infoLineHeight;
              }
            }

            const paddingY = 6;
            const cardHeight = Math.max(40, infoHeight + imagesHeight + paddingY * 2);

            // Verificar si necesitamos nueva página
            if (y + cardHeight > pageHeight - 20) {
              doc.addPage();
              // Encabezado de continuación
              doc.setFillColor(...lightGray);
              doc.rect(0, 0, pageWidth, 15, "F");
              doc.setTextColor(...textColor);
              doc.setFontSize(10);
              doc.setFont("helvetica", "italic");
              doc.text(`${categoria} (continuación)`, margin, 10);
              y = 25;
            }

            // Fondo del card
            doc.setFillColor(...lightGray);
            doc.roundedRect(margin, y, cardWidth, cardHeight - 4, 3, 3, "F");
            doc.setDrawColor(...borderColor);
            doc.roundedRect(margin, y, cardWidth, cardHeight - 4, 3, 3, "S");

            // Información del producto
            const infoX = margin + 6;
            let cursorY = y + paddingY + 2;

            doc.setTextColor(...textColor);
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(nombreLines.slice(0, 2), infoX, cursorY);
            cursorY += nombreLineCount * 6;

            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...mutedColor);
            doc.text(`Ref: ${producto.codigo || "N/A"}`, infoX, cursorY);
            cursorY += infoLineHeight;

            if (producto.tipo) {
              doc.text(`Tipo: ${producto.tipo}`, infoX, cursorY);
              cursorY += infoLineHeight;
            }

            const tallasLines = doc.splitTextToSize(
              tallasTexto,
              contentWidth - 12
            );
            // Mostrar TODAS las líneas de tallas (hasta 3 líneas máximo)
            const tallasLinesToShow = tallasLines.slice(0, 3);
            doc.text(tallasLinesToShow, infoX, cursorY);
            cursorY += infoLineHeight * tallasLinesToShow.length;

            if (disponibilidad !== null && !Number.isNaN(disponibilidad)) {
              doc.text(`Disponibilidad: ${disponibilidad}`, infoX, cursorY);
              cursorY += infoLineHeight;
            }

            if (!this.sinPrecios) {
              // Precios
              doc.setFontSize(14);
              doc.setFont("helvetica", "bold");
              if (this.esEmpleado) {
                doc.setTextColor(...primaryColor);
                doc.text(this.formatPrice(producto.precioVenta), infoX, cursorY + 2);
                cursorY += 7;

                if (producto.precioMayoreo) {
                  doc.setFontSize(10);
                  doc.setFont("helvetica", "normal");
                  doc.setTextColor(100, 116, 139);
                  doc.text(
                    `Docena (x12): ${this.formatPrice(producto.precioMayoreo)}`,
                    infoX,
                    cursorY
                  );
                  cursorY += infoLineHeight;
                }
              } else {
                doc.setTextColor(231, 76, 60);
                doc.text(this.formatPrice(producto.precioVenta2), infoX, cursorY + 2);
              }
            }

            // Galería de imágenes con tallas específicas
            if (images.length) {
              const gridWidth =
                imagesPerRow * imgSize + (imagesPerRow - 1) * imgGap;
              const gridStartX = margin + (cardWidth - gridWidth) / 2;
              const gridStartY = y + paddingY + infoHeight + 2;

              for (let i = 0; i < images.length; i++) {
                const imgInfo = images[i];
                const row = Math.floor(i / imagesPerRow);
                const col = i % imagesPerRow;
                const imgX = gridStartX + col * (imgSize + imgGap);
                const imgY = gridStartY + row * (rowHeight + rowGap);
                try {
                  const imgData = await this.loadImageAsBase64(imgInfo.url);
                  if (imgData) {
                    const aspectRatio = imgData.width / imgData.height;
                    let imgW = imgSize;
                    let imgH = imgSize;
                    if (aspectRatio > 1) {
                      imgH = imgSize / aspectRatio;
                    } else {
                      imgW = imgSize * aspectRatio;
                    }
                    const centeredX = imgX + (imgSize - imgW) / 2;
                    const centeredY = imgY + (imgSize - imgH) / 2;
                    doc.addImage(imgData.data, "JPEG", centeredX, centeredY, imgW, imgH);
                  }
                } catch (e) {
                  // Si falla la imagen, continuamos
                }

                // Mostrar tallas específicas de esta imagen (hasta 2 líneas)
                const tallasImagen = this.getTallasParaImagen(producto, imgInfo.key);
                doc.setFontSize(6);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(60, 60, 60);
                const tallaLabelY = imgY + imgSize + 3;
                const labelLines = doc.splitTextToSize(tallasImagen, imgSize + 4);
                // Mostrar hasta 2 líneas de tallas
                const linesToShow = labelLines.slice(0, 2);
                for (let lineIdx = 0; lineIdx < linesToShow.length; lineIdx++) {
                  doc.text(linesToShow[lineIdx], imgX + imgSize / 2, tallaLabelY + lineIdx * 3, { align: "center" });
                }
              }
            }

            y += cardHeight;
          }
        }

        // Página final
        doc.addPage();
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, pageHeight, "F");
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("Gracias por tu interés", pageWidth / 2, pageHeight / 2 - 8, { align: "center" });
        
        doc.setFontSize(14);
        doc.setFont("helvetica", "normal");
        doc.text("Catálogo de productos 2026", pageWidth / 2, pageHeight / 2 + 12, { align: "center" });

        // Descargar
        this.progresoTexto = "Descargando...";
        const fileName = `Catalogo_Productos_2026_${new Date()
          .toISOString()
          .split("T")[0]}.pdf`;
        doc.save(fileName);

      } catch (error) {
        console.error("Error generando PDF:", error);
        alert("Hubo un error al generar el PDF. Por favor, intente de nuevo.");
      } finally {
        this.generando = false;
        this.progreso = 0;
        this.progresoTexto = "";
      }
    },
  },
};
</script>

<style scoped>
.descargar-catalogo-container {
  min-height: 100vh;
  padding: 100px 20px 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.config-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.card-header i {
  font-size: 48px;
  color: #e74c3c;
}

.card-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.descripcion {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 30px;
  line-height: 1.6;
}

.opcion-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 10px;
}

.opcion-header i {
  font-size: 24px;
  color: #f59e0b;
}

/* Switch toggle */
.switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.switch-container input {
  display: none;
}

.switch {
  width: 50px;
  height: 26px;
  background: #cbd5e1;
  border-radius: 13px;
  position: relative;
  transition: background 0.3s;
}

.switch::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-container input:checked + .switch {
  background: #2c73d2;
}

.switch-container input:checked + .switch::after {
  transform: translateX(24px);
}

.switch-label {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

/* Grupo de opciones */
.opcion-grupo {
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
}

.opcion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.opcion-item .opcion-header {
  margin-bottom: 0;
}

.opcion-item .opcion-header i {
  color: #2c73d2;
}

.opcion-ayuda {
  margin: 12px 0 0;
  color: #64748b;
  font-size: 13px;
}

/* Selector de categorías */
.categorias-selector {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #cbd5e1;
}

.selector-titulo {
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 15px;
}

.categorias-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.categoria-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.categoria-checkbox:hover {
  border-color: #2c73d2;
}

.categoria-checkbox.selected {
  border-color: #2c73d2;
  background: #eff6ff;
}

.categoria-checkbox input {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.categoria-checkbox.selected .checkmark {
  background: #2c73d2;
  border-color: #2c73d2;
}

.categoria-checkbox.selected .checkmark::after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.categoria-nombre {
  font-size: 13px;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Resumen */
.resumen {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
}

.resumen h3 {
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.resumen ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.resumen li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #15803d;
  margin-bottom: 6px;
}

.resumen li i {
  color: #22c55e;
}

/* Botón de descarga */
.btn-descargar {
  width: 100%;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(231, 76, 60, 0.4);
}

.btn-descargar:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(231, 76, 60, 0.5);
}

.btn-descargar:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-descargar i {
  font-size: 20px;
}

/* Barra de progreso */
.progress-container {
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  margin-top: 15px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #2c73d2, #667eea);
  border-radius: 3px;
  transition: width 0.3s;
}

/* Advertencia */
.advertencia {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #dc2626;
  font-size: 13px;
  margin-top: 15px;
  padding: 12px;
  background: #fef2f2;
  border-radius: 8px;
}

/* Animaciones */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

/* Responsive */
@media (max-width: 600px) {
  .descargar-catalogo-container {
    padding: 80px 10px 20px;
  }

  .config-card {
    padding: 25px 20px;
    border-radius: 16px;
  }

  .card-header h1 {
    font-size: 20px;
  }

  .card-header i {
    font-size: 36px;
  }

  .categorias-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .opcion-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>

